import { ApiCall } from '../ApiClient';
import { BoardLiteData, ResultData, UserLiteData, WorkspaceLiteData } from '../ApiTypes';
import { dateTransformer } from '../ApiUtil';

const get = <TResponse>(def: Omit<ApiCall<never, TResponse>, 'method'>): ApiCall<never, TResponse> =>
    Object.assign(def, { method: 'get' });

const post = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'post' });

export type FeedbackBody = {
    type: { key: string, label: string },
    message: string,
};

export interface AccountPlanLiteData {
    name: string;
    label: string;
    features: Record<string, any>;
    quantity?: number;
}

export interface AccountTrialLiteData {
    state: string;
    lengthDays: number;
    expiresAt: Date;
    createdAt: Date;
}

export interface AccountLiteData {
    id: string;
    label: string;
    imageUrl: string;
    createdAt: Date;
}

export interface AccountLiteDataWithExternality extends AccountLiteData {
    external: boolean;
}

export interface AccountData extends AccountLiteData {
    podId: string;
    plan: AccountPlanLiteData;
    trial: AccountTrialLiteData;
    metadata: Record<string, any>;
    settings: Record<string, any>;
}

export interface HomeData {
    legacy?: boolean;
    account: AccountData;
    workspaces: WorkspaceLiteData[];
}

export interface UsageData {
    objectCount: number;
}

export interface BoardUsageData {
    limit: number;
    active: number;
    boards: BoardLiteData[];
}

export interface PreferencesData {
    announcement: string;
    editorModePopup: boolean;
    gfxLevel: number;
    color: string;
    handwriting: string;
    handwritingMode: any;
    haug: boolean;
    hideOthers: boolean;
    legacyBoards: boolean;
    mouseMode: any;
    scrooge2024: boolean;
    slapping: boolean;
    notificationUnreads: boolean;
    notificationAssignment: boolean;
    notificationDaily: boolean;
    notificationWeekly: boolean;
    notificationEmailMention: boolean;
}

export interface CredentialsData {
    id: string;
    type: string;
    key: string;
    value?: string;
}

export interface ExternalSystemConnectionData {
    id: string;
    userId: string;
    accountId: string;
    externalSystemId: string;
    externalSystemUrl: string;
    externalSystemType: string;
    expiresAt: Date;
    createdAt: Date;
}

export type PreferencesBody = Partial<PreferencesData>;

export interface MyBoardListQuery {
    filter?: string;
    sort?: string;
    offset?: string;
    limit?: string;
}

export interface ProfileUpdateBody {
    name?: string;
    email?: string;
    emailSubscribed?: boolean;
    color?: string;
    handwriting?: string;
}

export interface ProfileData extends UserLiteData {
    type: number;
    legacyId: string;
    emailSubscribed: boolean;
    emailVerified: boolean;
    accounts: AccountLiteDataWithExternality[];
    credentials: CredentialsData[];
    connections: ExternalSystemConnectionData[];
    // flags: Record<string, boolean>;
    integrations: {
        calendar: boolean,
    };
}

export const meEndpoints = {

    feedback: (body: FeedbackBody) => post<ResultData>({
        path: '/me.feedback',
        body,
    }),

    home: () => get<HomeData>({
        path: '/me.home',
        transform: dateTransformer(),
    }),

    info: () => get<ProfileData>({
        path: '/me.info',
        transform: dateTransformer(),
    }),

    update: (body: ProfileUpdateBody) => post<UserLiteData>({
        path: '/me.update',
        body,
        transform: dateTransformer(),
    }),

    usage: () => get<UsageData>({
        path: '/me.usage',
        transform: dateTransformer(),
    }),

    boardUsage: () => get<BoardUsageData>({
        path: '/me.usage.boards',
        transform: dateTransformer(),
    }),

    permissions: ([type, id]: ['board' | 'workspace', string] = [null, null]) => get<string[]>({
        path: '/me.permissions',
        query: !!type && !!id ? { [`${type}Id`]: id } : {},
    }),

    preferences: {
        info: () => get<PreferencesData>({
            path: '/me.preferences.info',
        }),
        update: (body: PreferencesBody) => post<ResultData>({
            path: '/me.preferences.update',
            body,
        }),
    },
};
